/**
 * SyncDashboardModal
 *
 * Comprehensive modal for managing integration sync operations.
 * Contains tabs for: Sync Now, Running Jobs, Sync History, and Mappings.
 */

import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import type { OrganizationAssetType } from "@/models/organization/dashboard-analytics/dashboard-analytics.model";
import { auth } from "@/services/auth-service";
import {
	getAllJobs,
	getRunningJobs,
	getRunningPhotoSyncJobs,
	getSyncHistory,
	killJob,
	killPhotoSyncJob,
} from "@/services/integration-service";
import { getOrganizationLocations } from "@/services/location-service";
import { getOrganizationAssetTypes } from "@/services/utility-service";
import {
	type ServiceChannelAssetType,
	getServiceChannelAssetTypes,
} from "@/services/service-channel-service";
import {
	CheckCircleOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	HistoryOutlined,
	LoadingOutlined,
	NodeIndexOutlined,
	PlayCircleOutlined,
	ReloadOutlined,
	StopOutlined,
	SyncOutlined,
	ThunderboltOutlined,
} from "@ant-design/icons";
import {
	Alert,
	Badge,
	Button,
	Checkbox,
	Col,
	Empty,
	Input,
	Modal,
	Popconfirm,
	Row,
	Select,
	Space,
	Spin,
	Table,
	Tabs,
	Tag,
	Tooltip,
	Typography,
	message,
} from "antd";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";

const { Text, Title } = Typography;

interface SyncDashboardModalProps {
	open: boolean;
	onClose: () => void;
	integrationId: number;
	integrationName: string;
}

interface Location {
	id: string;
	address: string;
	store_number?: string;
}

interface RunningJob {
	id: number;
	organizationId: number;
	integrationId: number;
	jobStatus: string;
	startedAt: string;
	finishedAt?: string | null;
	details?: any;
}

interface SyncHistoryItem {
	id: number;
	organization_id: number;
	sync_time: string;
	sync_status: string;
	job_id: number;
}

const SyncDashboardModal = ({ open, onClose, integrationId, integrationName }: SyncDashboardModalProps) => {
	// Sync Now state
	const [locations, setLocations] = useState<Location[]>([]);
	const [loadingLocations, setLoadingLocations] = useState(false);
	const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [syncing, setSyncing] = useState(false);
	const [syncMode, setSyncMode] = useState<"asset" | "photo" | "combined">("asset");
	const [forceOverride, setForceOverride] = useState(false);

	// Running Jobs state
	const [runningJobs, setRunningJobs] = useState<RunningJob[]>([]);
	const [runningPhotoJobs, setRunningPhotoJobs] = useState<RunningJob[]>([]);
	const [loadingJobs, setLoadingJobs] = useState(false);
	const [killingJobId, setKillingJobId] = useState<number | null>(null);
	const [killingPhotoJobId, setKillingPhotoJobId] = useState<number | null>(null);

	// Sync History state
	const [syncHistory, setSyncHistory] = useState<SyncHistoryItem[]>([]);
	const [loadingHistory, setLoadingHistory] = useState(false);

	// Mappings state
	const [assetTypes, setAssetTypes] = useState<OrganizationAssetType[]>([]);
	const [scAssetTypes, setScAssetTypes] = useState<ServiceChannelAssetType[]>([]);
	const [mappings, setMappings] = useState<Record<number, string | number>>({});
	const [loadingMappings, setLoadingMappings] = useState(false);
	const [savingMappings, setSavingMappings] = useState(false);
	const [mappingsLoaded, setMappingsLoaded] = useState(false);

	// Active tab
	const [activeTab, setActiveTab] = useState("sync");

	// ─── Data Fetchers ───────────────────────────

	const fetchLocations = useCallback(async () => {
		try {
			setLoadingLocations(true);
			const data = await getOrganizationLocations();
			if (Array.isArray(data) && data.length > 0) {
				setLocations(
					data.map((loc: any) => ({
						id: String(loc.id),
						address: `${loc.store_number ? `#${loc.store_number} - ` : ""}${loc.address1 || ""}${loc.city ? `, ${loc.city}` : ""}${loc.state ? ` ${loc.state}` : ""}`,
						store_number: loc.store_number,
					})),
				);
			}
		} catch (error) {
			console.error("Error fetching locations:", error);
		} finally {
			setLoadingLocations(false);
		}
	}, []);

	const fetchRunningJobs = useCallback(async () => {
		try {
			setLoadingJobs(true);
			const [jobsResp, photoJobsResp] = await Promise.all([
				getRunningJobs(),
				getRunningPhotoSyncJobs(),
			]);
			setRunningJobs(jobsResp?.data || []);
			setRunningPhotoJobs(photoJobsResp?.data || []);
		} catch (error) {
			console.error("Error fetching running jobs:", error);
		} finally {
			setLoadingJobs(false);
		}
	}, []);

	const fetchSyncHistory = useCallback(async () => {
		try {
			setLoadingHistory(true);
			const response = await getSyncHistory();
			if (response?.success && response?.data) {
				setSyncHistory(response.data);
			}
		} catch (error) {
			console.error("Error fetching sync history:", error);
		} finally {
			setLoadingHistory(false);
		}
	}, []);

	// Load data when modal opens
	useEffect(() => {
		if (open && integrationId) {
			fetchLocations();
			fetchRunningJobs();
			fetchSyncHistory();
			setActiveTab("sync");
			setSelectedLocations([]);
			setSearchTerm("");
			setSyncMode("asset");
		}
	}, [open, integrationId]);

	// ─── Sync Handler ────────────────────────────

	const handleSync = async () => {
		try {
			setSyncing(true);
			const token = await auth?.currentUser?.getIdToken();
			if (!token) throw new Error("No authentication token");

			let successMsg = "";

			if (syncMode === "photo") {
				// Photo sync only
				const params = new URLSearchParams();
				if (selectedLocations.length > 0) params.set("locationIds", selectedLocations.join(","));
				if (forceOverride) params.set("forceOverride", "true");
				const queryString = params.toString() ? `?${params.toString()}` : "";
				const resp = await fetch(
					`${VITE_CORE_API_URL}${CORE_URL}/outside-source/integration/photo-sync${queryString}`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
					},
				);
				if (!resp.ok) throw new Error("Photo sync failed");
				const data = await resp.json();
				successMsg = data.message || "Photo sync job queued";
			} else {
				// Asset sync (or combined: asset sync with auto photo sync)
				if (selectedLocations.length === 0) {
					message.warning("Please select at least one location");
					setSyncing(false);
					return;
				}
				const locationIds = selectedLocations.join(",");
				const overrideParam = forceOverride ? "&forceOverride=true" : "";
				const autoPhotoParam = syncMode === "combined" ? "&autoPhotoSync=true" : "";
				const resp = await fetch(
					`${VITE_CORE_API_URL}${CORE_URL}/outside-source/integration/sync?integrationId=${integrationId}&locationIds=${locationIds}${overrideParam}${autoPhotoParam}`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
					},
				);
				if (!resp.ok) throw new Error("Sync failed");
				const data = await resp.json();
				successMsg = data.message || "Sync job queued";
			}

			message.success(successMsg);
			setSelectedLocations([]);
			fetchRunningJobs();
			fetchSyncHistory();
			setActiveTab("jobs");
		} catch (error: any) {
			message.error(error.message || "Sync failed");
		} finally {
			setSyncing(false);
		}
	};

	// ─── Kill Jobs ───────────────────────────────

	const handleKillJob = async (jobId: number) => {
		try {
			setKillingJobId(jobId);
			await killJob(jobId);
			message.success(`Job ${jobId} killed`);
			fetchRunningJobs();
			fetchSyncHistory();
		} catch (error) {
			message.error("Failed to kill job");
		} finally {
			setKillingJobId(null);
		}
	};

	const handleKillPhotoJob = async (jobId: number) => {
		try {
			setKillingPhotoJobId(jobId);
			await killPhotoSyncJob(jobId);
			message.success(`Photo sync job ${jobId} killed`);
			fetchRunningPhotoSyncJobs();
		} catch (error) {
			message.error("Failed to kill photo sync job");
		} finally {
			setKillingPhotoJobId(null);
		}
	};

	const fetchRunningPhotoSyncJobs = async () => {
		try {
			const resp = await getRunningPhotoSyncJobs();
			setRunningPhotoJobs(resp?.data || []);
		} catch {}
	};

	// ─── Filtered Locations ──────────────────────

	const filteredLocations = locations.filter((loc) => {
		if (!searchTerm.trim()) return true;
		return loc.address.toLowerCase().includes(searchTerm.toLowerCase());
	});

	const allSelected =
		filteredLocations.length > 0 &&
		filteredLocations.every((loc) => selectedLocations.includes(loc.id));

	const handleSelectAll = () => {
		if (allSelected) {
			const filteredIds = new Set(filteredLocations.map((l) => l.id));
			setSelectedLocations((prev) => prev.filter((id) => !filteredIds.has(id)));
		} else {
			const newSelections = new Set([...selectedLocations, ...filteredLocations.map((l) => l.id)]);
			setSelectedLocations(Array.from(newSelections));
		}
	};

	// ─── Status helpers ──────────────────────────

	const getStatusTag = (status: string) => {
		switch (status?.toLowerCase()) {
			case "success":
			case "completed":
				return <Tag icon={<CheckCircleOutlined />} color="success">Success</Tag>;
			case "failed":
			case "error":
				return <Tag icon={<CloseCircleOutlined />} color="error">Failed</Tag>;
			case "running":
			case "in_progress":
				return <Tag icon={<LoadingOutlined spin />} color="processing">Running</Tag>;
			case "killed":
				return <Tag icon={<StopOutlined />} color="warning">Killed</Tag>;
			default:
				return <Tag>{status || "Unknown"}</Tag>;
		}
	};

	// ─── Running Jobs Count ──────────────────────

	const totalRunningJobs = runningJobs.length + runningPhotoJobs.length;

	// ─── Tab Content ─────────────────────────────

	const syncModeBtn = (mode: "asset" | "photo" | "combined", label: string, icon: React.ReactNode) => (
		<Button
			type={syncMode === mode ? "primary" : "default"}
			onClick={() => setSyncMode(mode)}
			icon={icon}
			style={syncMode === mode ? { background: "#92278F", borderColor: "#92278F" } : {}}
		>
			{label}
		</Button>
	);

	const renderSyncTab = () => (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			{/* Sync Type Toggle + Override */}
			<div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
				{syncModeBtn("asset", "Asset Sync", <SyncOutlined />)}
				{syncModeBtn("photo", "Photo Sync", <SyncOutlined />)}
				{syncModeBtn("combined", "Assets + Photos", <ThunderboltOutlined />)}
				<div style={{ marginLeft: "auto" }}>
					<Checkbox
						checked={forceOverride}
						onChange={(e) => setForceOverride(e.target.checked)}
					>
						<Tooltip title="Skip the freshness check and push all FAI data to Service Channel, even if Service Channel data is newer">
							<Text type="secondary" style={{ fontSize: 13 }}>Force Override</Text>
						</Tooltip>
					</Checkbox>
				</div>
			</div>

			{syncMode === "photo" && (
				<Alert
					type="info"
					showIcon
					message="Photo sync will push asset photos to the external platform. Location selection is optional — leave empty to sync all."
					style={{ marginBottom: 0 }}
				/>
			)}

			{syncMode === "combined" && (
				<Alert
					type="info"
					showIcon
					message="Combined sync will first sync assets, then automatically start photo sync once the asset sync completes successfully."
					style={{ marginBottom: 0 }}
				/>
			)}

			{/* Location Selection */}
			<div>
				<Text strong style={{ display: "block", marginBottom: 8 }}>
					{syncMode === "photo" ? "Locations (optional)" : "Select Locations to Sync"}
				</Text>
				<Input
					placeholder="Search locations..."
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					allowClear
					style={{ marginBottom: 8 }}
				/>
				<div
					style={{
						border: "1px solid #d9d9d9",
						borderRadius: 6,
						maxHeight: 280,
						overflowY: "auto",
						background: "#fafafa",
					}}
				>
					<div style={{ padding: "8px 12px", borderBottom: "1px solid #f0f0f0", background: "#fff" }}>
						<Checkbox checked={allSelected} onChange={handleSelectAll}>
							<Text type="secondary">
								Select All{searchTerm ? ` (${filteredLocations.length} matching)` : ` (${locations.length})`}
							</Text>
						</Checkbox>
						{selectedLocations.length > 0 && (
							<Tag color="blue" style={{ marginLeft: 8 }}>{selectedLocations.length} selected</Tag>
						)}
					</div>
					{loadingLocations ? (
						<div style={{ textAlign: "center", padding: 24 }}><Spin /></div>
					) : filteredLocations.length === 0 ? (
						<Empty description="No locations found" style={{ padding: 24 }} image={Empty.PRESENTED_IMAGE_SIMPLE} />
					) : (
						<div style={{ padding: "4px 12px" }}>
							{filteredLocations.map((loc) => (
								<div key={loc.id} style={{ padding: "4px 0" }}>
									<Checkbox
										checked={selectedLocations.includes(loc.id)}
										onChange={() => {
											setSelectedLocations((prev) =>
												prev.includes(loc.id)
													? prev.filter((id) => id !== loc.id)
													: [...prev, loc.id],
											);
										}}
									>
										<Text style={{ fontSize: 13 }}>{loc.address}</Text>
									</Checkbox>
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			{/* Sync Button */}
			<Button
				type="primary"
				size="large"
				icon={syncing ? <LoadingOutlined /> : <ThunderboltOutlined />}
				onClick={handleSync}
				loading={syncing}
				disabled={syncMode !== "photo" && selectedLocations.length === 0}
				block
				style={{ background: "#92278F", borderColor: "#92278F", height: 44 }}
			>
				{syncing
					? "Syncing..."
					: syncMode === "photo"
						? "Start Photo Sync"
						: syncMode === "combined"
							? `Sync Assets + Photos (${selectedLocations.length} Location(s))`
							: `Sync ${selectedLocations.length} Location(s)`}
			</Button>
		</div>
	);

	const renderJobsTab = () => (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Title level={5} style={{ margin: 0 }}>Currently Running Jobs</Title>
				<Button
					icon={<ReloadOutlined />}
					size="small"
					onClick={() => fetchRunningJobs()}
					loading={loadingJobs}
				>
					Refresh
				</Button>
			</div>

			{/* Integration Sync Jobs */}
			{runningJobs.length === 0 && runningPhotoJobs.length === 0 ? (
				<Alert
					type="info"
					showIcon
					message="No running jobs"
					description="There are currently no sync jobs running."
				/>
			) : (
				<>
					{runningJobs.length > 0 && (
						<>
							<Text strong>Asset Sync Jobs</Text>
							<Table
								dataSource={runningJobs}
								rowKey="id"
								size="small"
								pagination={false}
								columns={[
									{ title: "Job ID", dataIndex: "id", width: 80 },
									{
										title: "Started",
										dataIndex: "startedAt",
										render: (v: string) => v ? new Date(v).toLocaleString() : "—",
									},
									{
										title: "Status",
										dataIndex: "jobStatus",
										render: (s: string) => getStatusTag(s),
									},
									{
										title: "Action",
										width: 80,
										render: (_: any, record: RunningJob) => (
											<Popconfirm
												title="Kill this job?"
												onConfirm={() => handleKillJob(record.id)}
											>
												<Button
													danger
													size="small"
													icon={<StopOutlined />}
													loading={killingJobId === record.id}
												>
													Kill
												</Button>
											</Popconfirm>
										),
									},
								]}
							/>
						</>
					)}

					{runningPhotoJobs.length > 0 && (
						<>
							<Text strong>Photo Sync Jobs</Text>
							<Table
								dataSource={runningPhotoJobs}
								rowKey="id"
								size="small"
								pagination={false}
								columns={[
									{ title: "Job ID", dataIndex: "id", width: 80 },
									{
										title: "Started",
										dataIndex: "startedAt",
										render: (v: string) => v ? new Date(v).toLocaleString() : "—",
									},
									{
										title: "Status",
										dataIndex: "jobStatus",
										render: (s: string) => getStatusTag(s),
									},
									{
										title: "Action",
										width: 80,
										render: (_: any, record: RunningJob) => (
											<Popconfirm
												title="Kill this job?"
												onConfirm={() => handleKillPhotoJob(record.id)}
											>
												<Button
													danger
													size="small"
													icon={<StopOutlined />}
													loading={killingPhotoJobId === record.id}
												>
													Kill
												</Button>
											</Popconfirm>
										),
									},
								]}
							/>
						</>
					)}
				</>
			)}
		</div>
	);

	const renderHistoryTab = () => (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Title level={5} style={{ margin: 0 }}>Sync History</Title>
				<Button
					icon={<ReloadOutlined />}
					size="small"
					onClick={() => fetchSyncHistory()}
					loading={loadingHistory}
				>
					Refresh
				</Button>
			</div>

			{loadingHistory ? (
				<div style={{ textAlign: "center", padding: 48 }}><Spin size="large" /></div>
			) : syncHistory.length === 0 ? (
				<Empty description="No sync history yet" />
			) : (
				<Table
					dataSource={syncHistory}
					rowKey="id"
					size="small"
					pagination={{ pageSize: 10, showSizeChanger: false }}
					columns={[
						{
							title: "Job ID",
							dataIndex: "job_id",
							width: 80,
						},
						{
							title: "Sync Time",
							dataIndex: "sync_time",
							render: (v: string) => v ? new Date(v).toLocaleString() : "—",
							sorter: (a: SyncHistoryItem, b: SyncHistoryItem) =>
								new Date(a.sync_time).getTime() - new Date(b.sync_time).getTime(),
							defaultSortOrder: "descend",
						},
						{
							title: "Status",
							dataIndex: "sync_status",
							render: (s: string) => getStatusTag(s),
							filters: [
								{ text: "Success", value: "success" },
								{ text: "Failed", value: "failed" },
								{ text: "Running", value: "running" },
							],
							onFilter: (value: any, record: SyncHistoryItem) =>
								record.sync_status?.toLowerCase() === value,
						},
					]}
				/>
			)}
		</div>
	);

	// ─── Mappings ────────────────────────────────

	const fetchMappingsData = useCallback(async () => {
		try {
			setLoadingMappings(true);
			const [typesResp, scTypes, currentMappings] = await Promise.all([
				getOrganizationAssetTypes(true),
				getServiceChannelAssetTypes(),
				axios.get(`${VITE_CORE_API_URL}${CORE_URL}/organization/self/asset-mappings`, {
					headers: { Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}` },
				}),
			]);

			// Parse asset types
			if (Array.isArray(typesResp)) {
				setAssetTypes(typesResp as unknown as OrganizationAssetType[]);
			}

			setScAssetTypes(scTypes);

			// Parse existing mappings
			if (currentMappings.data?.success && currentMappings.data?.data?.assetTypeMap) {
				const existingMappings: Record<number, string | number> = {};
				const mapData = currentMappings.data.data.assetTypeMap;
				if (Array.isArray(mapData)) {
					mapData.forEach(([key, val]: [string, any]) => {
						const assetType = typesResp?.find((t: any) => t.name === key);
						if (assetType && val?.type) {
							const scType = scTypes.find((s: any) => s.name === val.type);
							if (scType) existingMappings[assetType.id] = scType.id;
						}
					});
				}
				setMappings(existingMappings);
			}

			setMappingsLoaded(true);
		} catch (error) {
			console.error("Error loading mappings:", error);
			message.error("Failed to load mappings data");
		} finally {
			setLoadingMappings(false);
		}
	}, []);

	const handleSaveMappings = async () => {
		try {
			setSavingMappings(true);
			const assetTypeMap = assetTypes
				.filter((t) => mappings[t.id] !== undefined)
				.map((t) => {
					const scType = scAssetTypes.find((sc) => sc.name === mappings[t.id] || sc.id === mappings[t.id]);
					if (!scType) return null;
					return [t.name, { type: scType.name, trade: scType.trade }];
				})
				.filter(Boolean);

			if (assetTypeMap.length === 0) {
				message.warning("No mappings set");
				return;
			}

			const response = await axios.put(
				`${VITE_CORE_API_URL}${CORE_URL}/organization/self/asset-mappings`,
				{ assetTypeMap },
				{ headers: { Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}` } },
			);

			if (response.data?.success) {
				message.success("Mappings saved successfully");
			} else {
				throw new Error(response.data?.message || "Failed to save");
			}
		} catch (error: any) {
			message.error(error.message || "Failed to save mappings");
		} finally {
			setSavingMappings(false);
		}
	};

	// Load mappings when switching to that tab
	useEffect(() => {
		if (activeTab === "mappings" && !mappingsLoaded && open) {
			fetchMappingsData();
		}
	}, [activeTab, mappingsLoaded, open]);

	const renderMappingsTab = () => (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<div>
					<Title level={5} style={{ margin: 0 }}>Asset Type Mappings</Title>
					<Text type="secondary" style={{ fontSize: 12 }}>
						Map your FacilityAI asset types to {integrationName} asset types
					</Text>
				</div>
				<Button
					type="primary"
					onClick={handleSaveMappings}
					loading={savingMappings}
					disabled={loadingMappings}
					style={{ background: "#92278F", borderColor: "#92278F" }}
				>
					Save Mappings
				</Button>
			</div>

			{loadingMappings ? (
				<div style={{ textAlign: "center", padding: 48 }}><Spin size="large" /></div>
			) : (
				<>
					{/* Column headers */}
					<Row gutter={16} style={{ fontWeight: 600, padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}>
						<Col span={12}>FacilityAI Asset Type</Col>
						<Col span={12}>{integrationName} Asset Type</Col>
					</Row>

					{/* Scrollable mappings */}
					<div style={{ maxHeight: 380, overflowY: "auto" }}>
						{assetTypes.map((type) => (
							<Row key={type.id} gutter={16} style={{ padding: "6px 0", borderBottom: "1px solid #fafafa" }} align="middle">
								<Col span={12}>
									<Text style={{ fontSize: 13 }}>{type.name}</Text>
								</Col>
								<Col span={12}>
									<Select
										showSearch
										optionFilterProp="label"
										filterOption={(input, option) =>
											String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
										}
										placeholder="Select type"
										value={mappings[type.id]}
										onChange={(v) => setMappings((prev) => ({ ...prev, [type.id]: v }))}
										options={scAssetTypes.map((s) => ({ value: s.id, label: s.name }))}
										style={{ width: "100%" }}
										size="small"
										allowClear
									/>
								</Col>
							</Row>
						))}
					</div>
				</>
			)}
		</div>
	);

	// ─── Tab Items ───────────────────────────────

	const tabItems = [
		{
			key: "sync",
			label: (
				<span>
					<ThunderboltOutlined style={{ marginRight: 6 }} />
					Sync Now
				</span>
			),
			children: renderSyncTab(),
		},
		{
			key: "jobs",
			label: (
				<span>
					<PlayCircleOutlined style={{ marginRight: 6 }} />
					Running Jobs
					{totalRunningJobs > 0 && (
						<Badge count={totalRunningJobs} size="small" style={{ marginLeft: 6 }} />
					)}
				</span>
			),
			children: renderJobsTab(),
		},
		{
			key: "history",
			label: (
				<span>
					<HistoryOutlined style={{ marginRight: 6 }} />
					History
				</span>
			),
			children: renderHistoryTab(),
		},
		// Mappings tab — only show for integrations that support it (ServiceChannel = 1)
		...(integrationId === 1
			? [
					{
						key: "mappings",
						label: (
							<span>
								<NodeIndexOutlined style={{ marginRight: 6 }} />
								Mappings
							</span>
						),
						children: renderMappingsTab(),
					},
				]
			: []),
	];

	return (
		<Modal
			title={
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<SyncOutlined style={{ color: "#92278F" }} />
					<span>{integrationName} — Sync Dashboard</span>
				</div>
			}
			open={open}
			onCancel={onClose}
			footer={null}
			width={720}
			destroyOnClose
			styles={{ body: { padding: "0 24px 24px", minHeight: 400 } }}
		>
			<Tabs
				activeKey={activeTab}
				onChange={setActiveTab}
				items={tabItems}
				style={{ marginTop: -8 }}
			/>
		</Modal>
	);
};

export { SyncDashboardModal };
